/**
 * A collection of miscellaneous math utilities that currently don't have their own category
 */
import { Vector2, Vector3 } from "three";
export class MathUtils {
    /**
     *
     * @param a
     * @param b
     * @param relativeEpsilon
     * @param absoluteEpsilon
     */
    static equalsEpsilon(a, b, relativeEpsilon = 0, absoluteEpsilon = relativeEpsilon) {
        const absDiff = Math.abs(a - b);
        return absDiff <= absoluteEpsilon || absDiff <= relativeEpsilon * Math.max(Math.abs(a), Math.abs(b));
    }
    /**
     * Project a sphere's radius with radius *r* into screen space at the given distance *d* from the camera.
     *
     * The *halfScreenHeight* parameter is multiplied with the result of the projection function to produce the
     * projected radius of the sphere in pixels. The parameter defaults to 1 which becomes an identity op (multiply by
     * one) and so leaving *halfScreenHeight* unspecified will allow getting the raw result of the projection
     *
     * @param fov The vertical FOV of the projection matrix in degrees
     * @param d The distance from the camera to the center of the sphere
     * @param r The radius of the sphere to project
     * @param halfScreenHeight The height of the screen in pixels / 2.
     */
    static computeProjectedRadius(fov, d, r, halfScreenHeight = 1) {
        const halfFovRadians = ((fov / 2) * Math.PI) / 180.0;
        const projectedRadius = ((1.0 / Math.tan(halfFovRadians)) * r) / Math.sqrt(d * d - r * r);
        return projectedRadius * halfScreenHeight; // Right
    }
    /**
     * Converts a position in screen coordinates (pixel coordinates) into NDC coordinates. This essentially just applies
     * the viewport transform to a screen coordinate. The screen coordinate is normalized into a -1 to 1 range based on
     * the screen dimensions
     * @param x
     * @param y
     * @param sizeX
     * @param sizeY
     * @param result
     */
    static screenCoordsToNDC(x, y, sizeX, sizeY, result) {
        // Convert our pixel coordinates into NDC
        result.x = -1 + 2 * (x / sizeX);
        result.y = -1 + 2 * (y / sizeY);
        return result;
    }
    /**
     * Decode a value sampled from the logarithmic depth buffer to retrieve the raw Z value. The returned value returns
     * the distance from the camera to the point on whichever surface was sampled to produce the depth value.
     *
     * To convert this back to a full world space position this value should be multiplied with the direction from the
     * camera's world space position to the pixel coordinate for the depth value.
     *
     * This only works for logarithmic depth buffer.
     *
     * @param depth The depth value extracted from the depth buffer
     * @param far The far clipping plane of the projection matrix
     */
    static decodeLogarithmicDepth(depth, near, far) {
        const z = Math.pow(2.0, depth * Math.log2(far + 1.0)) - 1.0;
        const a = far / (far - near);
        const b = (far * near) / (near - far);
        return a + b / z;
    }
    /**
     * Converts degrees into radians
     * @param degrees Angle in degrees
     * @returns result Angle in radians
     */
    static degreesToRadians(degrees) {
        return degrees * (Math.PI / 180);
    }
    /**
     * Converts radians into degrees
     * @param radians Angle in radians
     * @returns result Angle in degrees
     */
    static radiansToDegrees(radians) {
        return radians * (180 / Math.PI);
    }
    /**
     * Projects the geometric error of a tile into screen space error.
     * @param geometricError The geometric error of the tile
     * @param screenHeight The height of the screen image in pixels
     * @param distance The distance from the camera to the tile
     * @param fov The vertical fov of the camera (fovy) in radians
     */
    static projectGeometricError(geometricError, screenHeight, distance, fov) {
        return (geometricError * screenHeight) / (distance * 2 * Math.tan(fov / 2));
    }
    /**
     * Utility function for rotating about the Z axis
     * @param v Generic vector with X and Y component to apply rotation to
     * @param angle The angle of the rotation in radians
     */
    static rotateAboutZ(v, angle) {
        const srcX = v.x;
        const srcY = v.y;
        const cosX = srcX * Math.cos(angle);
        const sinX = srcX * Math.sin(angle);
        const cosZ = srcY * Math.cos(angle);
        const sinZ = srcY * Math.sin(angle);
        v.x = -sinZ + cosX;
        v.y = cosZ + sinX;
    }
    /**
     * Calculate the angle between two points.
     *
     * @param p1
     * @param p2
     * @returns
     */
    static angle2(p1, p2) {
        const v1 = new Vector2(p1.x, p1.y);
        const v2 = new Vector2(p2.x, p2.y);
        v2.sub(v1); // sets v2 to be our chord
        v2.normalize();
        if (v2.y < 0)
            return -Math.acos(v2.x);
        return Math.acos(v2.x);
    }
    /**
     * Compute polar coordinates.
     *
     * @param point
     * @param distance
     * @param angle
     * @returns
     */
    static polar(point, distance, angle) {
        return new Vector2(point.x + distance * Math.cos(angle), point.y + distance * Math.sin(angle));
    }
}
MathUtils.EPSILON1 = 0.1;
MathUtils.EPSILON2 = 0.01;
MathUtils.EPSILON3 = 0.001;
MathUtils.EPSILON4 = 0.0001;
MathUtils.EPSILON5 = 0.00001;
MathUtils.EPSILON6 = 0.000001;
MathUtils.EPSILON7 = 0.0000001;
MathUtils.EPSILON8 = 0.00000001;
MathUtils.EPSILON9 = 0.000000001;
MathUtils.EPSILON10 = 0.0000000001;
MathUtils.EPSILON11 = 0.00000000001;
MathUtils.EPSILON12 = 0.000000000001;
MathUtils.EPSILON13 = 0.0000000000001;
MathUtils.EPSILON14 = 0.00000000000001;
MathUtils.EPSILON15 = 0.000000000000001;
MathUtils.EPSILON16 = 0.0000000000000001;
MathUtils.EPSILON17 = 0.00000000000000001;
MathUtils.EPSILON18 = 0.000000000000000001;
MathUtils.EPSILON19 = 0.0000000000000000001;
MathUtils.EPSILON20 = 0.00000000000000000001;
MathUtils.EPSILON21 = 0.000000000000000000001;
MathUtils.ZERO_2 = new Vector2(0, 0);
MathUtils.ZERO_3 = new Vector3(0, 0, 0);
MathUtils.X_AXIS = new Vector3(1, 0, 0);
MathUtils.Y_AXIS = new Vector3(0, 1, 0);
MathUtils.Z_AXIS = new Vector3(0, 0, 1);
